import {
  PaymentMethodFragment,
  PaymentMethodStatus,
  useStripeSetupIntentSecretQuery,
} from '@alice-financial/alice-sdk'
import {
  Container,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Typography,
} from '@alice-financial/pretext-ui'
import AddIcon from '@mui/icons-material/Add'
import * as React from 'react'
import { useIssuingClientSecret } from '../useIssuingClientSecret'
import { Elements } from '@stripe/react-stripe-js'
import { AddNewACHPaymentSource, AddNewStripePaymentMethod } from './AddNewPaymentMethod'
import { useOrgId } from '../../useOrgId'
import { isConnectable } from './fundingUtils'
import { TypedBankableEntity } from './types'
import { useConnectPaymentMethod } from './useConnectPaymentMethod'
import { useIssuingStripe } from '../../../externalServices/stripe/useLoadStripe'

import { useAcceptACHMandate } from '../../../externalServices/stripe/useAcceptACHMandate'

// Payment method with guaranteed non-null ID
type PaymentMethodWithId = PaymentMethodFragment & { id: number }

type ConnectablePaymentMethodListProps = {
  connectablePaymentMethods: Array<PaymentMethodWithId>
  entity: TypedBankableEntity
  onSuccess?: () => void
}

type PaymentMethodListItemProps = {
  method: PaymentMethodWithId
  onClick: () => void
}

type BasePaymentMethodItemProps = {
  method: PaymentMethodWithId
  entity: TypedBankableEntity
  onSuccess?: () => void
}

type SimpleConnectablePaymentMethodItemProps = BasePaymentMethodItemProps

type PendingMandateContentProps = BasePaymentMethodItemProps & {
  clientSecret: string | undefined
  orgId: number
}

// Shared UI component for payment method list items
const PaymentMethodListItem = ({ method, onClick }: PaymentMethodListItemProps) => {
  const { id: paymentMethodId } = method

  return (
    <ListItem key={paymentMethodId} disablePadding disableGutters>
      <ListItemButton onClick={onClick}>
        <ListItemIcon>
          <AddIcon color="primary" />
        </ListItemIcon>
        <ListItemText
          primary={
            <Typography variant="body2" fontWeight="bold" color="primary" width="100%">
              {method.descriptor}
            </Typography>
          }
        />
      </ListItemButton>
    </ListItem>
  )
}

// Simple component for Active payment methods (no Stripe ACH mandate acceptance needed)
const SimpleConnectablePaymentMethodItem = ({
  method,
  entity,
  onSuccess,
}: SimpleConnectablePaymentMethodItemProps) => {
  const { id: paymentMethodId } = method
  const { mutate: connectPaymentMethod } = useConnectPaymentMethod({ owner: entity }, { onSuccess })

  const handleConnect = () => {
    connectPaymentMethod({ paymentMethodId })
  }

  return <PaymentMethodListItem method={method} onClick={handleConnect} />
}

// Inner component for accounts that have been pre-created from billing connect, but still need
// ACH mandate acceptance. This must be nested inside an Elements wrapper, since the call to accept
// the mandate requires Stripe context.
const PendingMandateContent = ({
  method,
  clientSecret,
  orgId,
  entity,
  onSuccess,
}: PendingMandateContentProps) => {
  const { id: paymentMethodId } = method
  const { mutate: connectPaymentMethod } = useConnectPaymentMethod({ owner: entity }, { onSuccess })
  const invalidationKeys = [useStripeSetupIntentSecretQuery.getKey({ orgId })]
  const { mutate: acceptAndPersist } = useAcceptACHMandate(() => undefined, invalidationKeys)

  const acceptMandateAndConnect = () => {
    if (clientSecret) {
      acceptAndPersist({ clientSecret })
    }
    connectPaymentMethod({ paymentMethodId })
  }

  return <PaymentMethodListItem method={method} onClick={acceptMandateAndConnect} />
}

type PendingMandateWrapperProps = {
  method: PaymentMethodWithId
  companyAccountId: number | null | undefined
  stripeAccountId: string | null | undefined
  orgId: number
  entity: TypedBankableEntity
  onSuccess?: () => void
}

const PendingMandateWrapper = ({
  method,
  companyAccountId,
  stripeAccountId,
  orgId,
  entity,
  onSuccess,
}: PendingMandateWrapperProps) => {
  const { data: issuingSecret } = useIssuingClientSecret(companyAccountId, method.stripeSystemId)
  const stripePromise = useIssuingStripe({ stripeAccount: stripeAccountId })

  return (
    <Elements stripe={stripePromise} options={{ clientSecret: issuingSecret }}>
      <PendingMandateContent
        method={method}
        clientSecret={issuingSecret}
        orgId={orgId}
        entity={entity}
        onSuccess={onSuccess}
      />
    </Elements>
  )
}

const ConnectablePaymentMethodList = ({
  connectablePaymentMethods,
  entity,
  onSuccess,
}: ConnectablePaymentMethodListProps) => {
  const orgId = useOrgId()
  const { data: stripeSetupIntentSecretData } = useStripeSetupIntentSecretQuery({ orgId })
  const companyAccount = stripeSetupIntentSecretData?.organization?.aliceCardProgram.companyAccount

  // Separate payment methods by status for optimized rendering
  const activePaymentMethods = (connectablePaymentMethods || []).filter(
    (method) => method.status === PaymentMethodStatus.Active
  )
  const pendingMandatePaymentMethods = (connectablePaymentMethods || []).filter(
    (method) => method.status === PaymentMethodStatus.PendingMandateAcceptance
  )

  return (
    <Paper sx={{ mb: 2 }}>
      <List disablePadding>
        {/* Render active methods without Stripe setup */}
        {activePaymentMethods.map((method) => (
          <SimpleConnectablePaymentMethodItem
            key={method.id}
            method={method}
            entity={entity}
            onSuccess={onSuccess}
          />
        ))}

        {/* Render pending mandate methods with Stripe setup */}
        {pendingMandatePaymentMethods.map((method) => (
          <PendingMandateWrapper
            key={method.id}
            method={method}
            companyAccountId={companyAccount?.id}
            stripeAccountId={companyAccount?.stripeAccountId}
            orgId={orgId}
            entity={entity}
            onSuccess={onSuccess}
          />
        ))}
      </List>
    </Paper>
  )
}

type ConnectPaymentMethodProps = {
  entity: TypedBankableEntity
  availablePaymentMethods: Array<PaymentMethodFragment> | undefined | null
  currentPaymentMethod: PaymentMethodFragment | undefined
  onSuccess?: () => void
  mayConnectToStripe: boolean
}

export const ConnectPaymentMethod = ({
  entity,
  availablePaymentMethods,
  currentPaymentMethod,
  onSuccess,
  mayConnectToStripe,
}: ConnectPaymentMethodProps) => {
  const connectablePaymentMethods = (availablePaymentMethods || [])
    .filter((method): method is PaymentMethodWithId => method.id != null)
    .filter(isConnectable(currentPaymentMethod))
  const willBeFirstPaymentMethod = (availablePaymentMethods || []).length === 0

  return (
    <>
      <Container>
        {(connectablePaymentMethods || []).length > 0 && (
          <>
            <ConnectablePaymentMethodList
              connectablePaymentMethods={connectablePaymentMethods}
              entity={entity}
              onSuccess={onSuccess}
            />
            <Typography textAlign="center" fontWeight="bold">
              or
            </Typography>
          </>
        )}
      </Container>
      <Container sx={{ my: 2 }}>
        {mayConnectToStripe ? (
          <AddNewStripePaymentMethod
            currentPaymentMethod={currentPaymentMethod}
            onSuccess={onSuccess}
            entity={entity}
            willBeFirstPaymentMethod={willBeFirstPaymentMethod}
          />
        ) : (
          <AddNewACHPaymentSource
            entity={entity}
            onSuccess={onSuccess}
            currentPaymentMethod={currentPaymentMethod}
            willBeFirstPaymentMethod={willBeFirstPaymentMethod}
          />
        )}
      </Container>
    </>
  )
}
